import { createContext, useContext, useState, useEffect, type ReactNode } from "react";

export type Lang = "en" | "ja" | "nl" | "fr";

export const LANG_OPTIONS: { code: Lang; label: string; short: string }[] = [
  { code: "en", label: "English", short: "EN" },
  { code: "ja", label: "日本語", short: "JP" },
  { code: "nl", label: "Nederlands", short: "NL" },
  { code: "fr", label: "Français", short: "FR" },
];

const translations = {
  // Navigation
  "nav.shop": { en: "Shop", ja: "ショップ", nl: "Winkel", fr: "Boutique" },
  "nav.workshops": { en: "Workshops", ja: "ワークショップ", nl: "Workshops", fr: "Ateliers" },
  "nav.journal": { en: "Journal", ja: "ジャーナル", nl: "Journaal", fr: "Journal" },
  "nav.wholesale": { en: "Wholesale", ja: "卸売り", nl: "Groothandel", fr: "Vente en gros" },
  "nav.about": { en: "About", ja: "私たちについて", nl: "Over ons", fr: "À propos" },
  "nav.stories": { en: "Stories", ja: "ストーリー", nl: "Verhalen", fr: "Histoires" },
  "nav.faq": { en: "FAQ", ja: "よくある質問", nl: "FAQ", fr: "FAQ" },
  "nav.search": { en: "Search", ja: "検索", nl: "Zoeken", fr: "Rechercher" },
  "nav.cart": { en: "Cart", ja: "カート", nl: "Winkelwagen", fr: "Panier" },
  "nav.account": { en: "Account", ja: "アカウント", nl: "Account", fr: "Compte" },
  "nav.menu": { en: "Menu", ja: "メニュー", nl: "Menu", fr: "Menu" },
  "nav.policies": { en: "Policies", ja: "ポリシー", nl: "Beleid", fr: "Politiques" },
  "nav.press": { en: "Press", ja: "プレス", nl: "Pers", fr: "Presse" },

  // Hero
  "hero.cta_default": { en: "Discover Matcha", ja: "抹茶を発見", nl: "Ontdek Matcha", fr: "Découvrir le Matcha" },

  // Product Section
  "products.label": { en: "Curated for you", ja: "あなたのために", nl: "Speciaal voor jou", fr: "Sélectionné pour vous" },
  "products.title": { en: "Our Collection", ja: "コレクション", nl: "Onze Collectie", fr: "Notre Collection" },
  "products.see_all": { en: "See All Products", ja: "すべて見る", nl: "Alle producten", fr: "Voir tous les produits" },
  "products.all": { en: "All", ja: "すべて", nl: "Alles", fr: "Tout" },
  "products.added": { en: "added to cart", ja: "カートに追加されました", nl: "toegevoegd aan winkelwagen", fr: "ajouté au panier" },
  "products.sold_out": { en: "Sold Out", ja: "完売", nl: "Uitverkocht", fr: "Épuisé" },
  "products.low_stock": { en: "Low Stock", ja: "在庫わずか", nl: "Beperkte voorraad", fr: "Stock limité" },
  "products.add_to_cart": { en: "Add to Cart", ja: "カートに追加", nl: "In winkelwagen", fr: "Ajouter au panier" },
  "products.search_placeholder": { en: "Search products...", ja: "商品を検索...", nl: "Producten zoeken...", fr: "Rechercher des produits..." },
  "products.no_results": { en: "No products found", ja: "商品が見つかりません", nl: "Geen producten gevonden", fr: "Aucun produit trouvé" },
  "products.filter_by_price": { en: "Price range", ja: "価格帯", nl: "Prijsklasse", fr: "Gamme de prix" },

  // Product Detail
  "detail.back": { en: "Home", ja: "ホーム", nl: "Home", fr: "Accueil" },
  "detail.details": { en: "Product Details", ja: "商品詳細", nl: "Productdetails", fr: "Détails du produit" },
  "detail.related": { en: "You may also like", ja: "おすすめ商品", nl: "Misschien vind je dit ook leuk", fr: "Vous aimerez aussi" },
  "detail.related_title": { en: "Related Products", ja: "関連商品", nl: "Gerelateerde producten", fr: "Produits associés" },
  "detail.out_of_stock": { en: "Currently out of stock", ja: "現在在庫切れ", nl: "Momenteel niet op voorraad", fr: "Actuellement en rupture de stock" },
  "detail.only_left": { en: "left in stock", ja: "在庫残り", nl: "nog op voorraad", fr: "restant en stock" },
  "detail.order_whatsapp": { en: "Order via WhatsApp", ja: "WhatsAppで注文", nl: "Bestellen via WhatsApp", fr: "Commander via WhatsApp" },
  "detail.not_found": { en: "Product not found", ja: "商品が見つかりません", nl: "Product niet gevonden", fr: "Produit introuvable" },
  "detail.back_shop": { en: "Back to Shop", ja: "ショップに戻る", nl: "Terug naar winkel", fr: "Retour à la boutique" },
  "detail.recently_viewed": { en: "Recently Viewed", ja: "最近見た商品", nl: "Recent bekeken", fr: "Récemment consultés" },

  // Reviews
  "reviews.title": { en: "Customer Reviews", ja: "カスタマーレビュー", nl: "Klantbeoordelingen", fr: "Avis clients" },
  "reviews.write": { en: "Write a Review", ja: "レビューを書く", nl: "Schrijf een beoordeling", fr: "Écrire un avis" },
  "reviews.no_reviews": {
    en: "No reviews yet. Be the first to share your experience.",
    ja: "まだレビューはありません。最初のレビューを書いてみましょう。",
    nl: "Nog geen beoordelingen. Wees de eerste om je ervaring te delen.",
    fr: "Aucun avis pour le moment. Soyez le premier à partager votre expérience.",
  },
  "reviews.your_name": { en: "Your Name", ja: "お名前", nl: "Je naam", fr: "Votre nom" },
  "reviews.your_review": { en: "Your Review", ja: "レビュー", nl: "Je beoordeling", fr: "Votre avis" },
  "reviews.submit": { en: "Submit Review", ja: "レビューを投稿", nl: "Beoordeling versturen", fr: "Soumettre l'avis" },
  "reviews.pending": { en: "Pending approval", ja: "承認待ち", nl: "In afwachting van goedkeuring", fr: "En attente d'approbation" },

  // Cart
  "cart.title": { en: "Your Cart", ja: "カート", nl: "Winkelwagen", fr: "Votre panier" },
  "cart.empty": { en: "Your cart is empty", ja: "カートは空です", nl: "Je winkelwagen is leeg", fr: "Votre panier est vide" },
  "cart.empty_desc": { en: "Discover our collection and add something beautiful.", ja: "コレクションを見て、素敵なものを追加してください。", nl: "Ontdek onze collectie en voeg iets moois toe.", fr: "Découvrez notre collection et ajoutez quelque chose de beau." },
  "cart.subtotal": { en: "Subtotal", ja: "小計", nl: "Subtotaal", fr: "Sous-total" },
  "cart.total": { en: "Total", ja: "合計", nl: "Totaal", fr: "Total" },
  "cart.proceed": { en: "Proceed to Checkout", ja: "会計に進む", nl: "Afrekenen", fr: "Passer à la caisse" },

  // Checkout
  "checkout.title": { en: "Checkout", ja: "チェックアウト", nl: "Afrekenen", fr: "Paiement" },
  "checkout.your_details": { en: "Your Details", ja: "お客様情報", nl: "Je gegevens", fr: "Vos coordonnées" },
  "checkout.name": { en: "Name", ja: "お名前", nl: "Naam", fr: "Nom" },
  "checkout.email": { en: "Email", ja: "メール", nl: "E-mail", fr: "E-mail" },
  "checkout.phone": { en: "Phone", ja: "電話番号", nl: "Telefoon", fr: "Téléphone" },
  "checkout.delivery": { en: "Delivery Preference", ja: "配送方法", nl: "Bezorgvoorkeur", fr: "Préférence de livraison" },
  "checkout.shipping": { en: "Shipping", ja: "配送", nl: "Verzending", fr: "Livraison" },
  "checkout.pickup": { en: "Pickup", ja: "店頭受取", nl: "Ophalen", fr: "Retrait" },
  "checkout.country": { en: "Country", ja: "国", nl: "Land", fr: "Pays" },
  "checkout.address": { en: "Address", ja: "住所", nl: "Adres", fr: "Adresse" },
  "checkout.shipping_address_label": { en: "Shipping address", ja: "配送先住所", nl: "Verzendadres", fr: "Adresse de livraison" },
  "checkout.notes": { en: "Notes for the Studio", ja: "スタジオへのメモ", nl: "Opmerkingen voor de studio", fr: "Notes pour le studio" },
  "checkout.send_order": { en: "Send Order Request", ja: "注文リクエストを送信", nl: "Bestelling versturen", fr: "Envoyer la commande" },
  "checkout.sending": { en: "Sending...", ja: "送信中...", nl: "Verzenden...", fr: "Envoi en cours..." },
  "checkout.no_payment": {
    en: "No payment required — we will confirm your order by email.",
    ja: "お支払い不要 — メールで注文を確認いたします。",
    nl: "Geen betaling vereist — we bevestigen je bestelling per e-mail.",
    fr: "Aucun paiement requis — nous confirmerons votre commande par e-mail.",
  },
  "checkout.summary": { en: "Order Summary", ja: "注文概要", nl: "Besteloverzicht", fr: "Résumé de la commande" },
  "checkout.promo_code": { en: "Have a promo code?", ja: "プロモコードをお持ちですか？", nl: "Heb je een promotiecode?", fr: "Vous avez un code promo ?" },
  "checkout.apply": { en: "Apply", ja: "適用", nl: "Toepassen", fr: "Appliquer" },
  "checkout.discount": { en: "Discount", ja: "割引", nl: "Korting", fr: "Réduction" },
  "checkout.promo_placeholder": { en: "Enter code", ja: "コードを入力", nl: "Voer code in", fr: "Entrez le code" },
  "checkout.promo_invalid": { en: "Invalid or expired code", ja: "無効または期限切れのコード", nl: "Ongeldige of verlopen code", fr: "Code invalide ou expiré" },
  "checkout.promo_applied": { en: "Promo code applied!", ja: "プロモコードが適用されました！", nl: "Promotiecode toegepast!", fr: "Code promo appliqué !" },
  "checkout.cart_empty": { en: "Your cart is empty", ja: "カートは空です", nl: "Je winkelwagen is leeg", fr: "Votre panier est vide" },
  "checkout.cart_empty_desc": {
    en: "Add some items to your cart before checking out.",
    ja: "チェックアウト前にカートに商品を追加してください。",
    nl: "Voeg eerst producten toe aan je winkelwagen.",
    fr: "Ajoutez des articles à votre panier avant de passer commande.",
  },

  // Confirmation
  "confirmation.title": { en: "Thank You", ja: "ありがとうございます", nl: "Dank je wel", fr: "Merci" },
  "confirmation.message": {
    en: "Your order request has been received. We will review it and contact you shortly.",
    ja: "ご注文リクエストを受け付けました。確認後、ご連絡いたします。",
    nl: "Je bestelling is ontvangen. We nemen binnenkort contact met je op.",
    fr: "Votre commande a été reçue. Nous vous contacterons prochainement.",
  },
  "confirmation.summary": { en: "Order Summary", ja: "注文概要", nl: "Besteloverzicht", fr: "Résumé de la commande" },
  "confirmation.continue": { en: "Continue Browsing", ja: "引き続き閲覧する", nl: "Verder winkelen", fr: "Continuer vos achats" },
  "confirmation.whatsapp": { en: "Send via WhatsApp", ja: "WhatsAppで送信", nl: "Verzenden via WhatsApp", fr: "Envoyer via WhatsApp" },

  // Editorial / About
  "editorial.label": { en: "The Philosophy", ja: "哲学", nl: "De filosofie", fr: "La philosophie" },

  // Workshops
  "workshops.label": { en: "Experiences", ja: "体験", nl: "Ervaringen", fr: "Expériences" },
  "workshops.title": { en: "Upcoming Workshops", ja: "今後のワークショップ", nl: "Aankomende workshops", fr: "Prochains ateliers" },
  "workshops.reserve": { en: "Reserve a Spot", ja: "席を予約", nl: "Reserveer een plek", fr: "Réserver une place" },
  "workshops.full": { en: "Full", ja: "満席", nl: "Vol", fr: "Complet" },
  "workshops.join_waitlist": { en: "Join waitlist", ja: "キャンセル待ち", nl: "Op wachtlijst", fr: "Rejoindre la liste d'attente" },
  "workshops.see_all": { en: "See All Workshops", ja: "すべてのワークショップ", nl: "Alle workshops", fr: "Tous les ateliers" },

  // Journal
  "journal.label": { en: "Stories & Rituals", ja: "物語と儀式", nl: "Verhalen & rituelen", fr: "Histoires & rituels" },
  "journal.title": { en: "The Journal", ja: "ジャーナル", nl: "Ons Verhaal", fr: "Le Journal" },
  "journal.empty": {
    en: "New stories are being written. Check back soon.",
    ja: "新しい記事を準備中です。",
    nl: "Nieuwe verhalen worden geschreven. Kom snel terug.",
    fr: "De nouvelles histoires sont en cours d'écriture. Revenez bientôt.",
  },
  "journal.read_more": { en: "Read More", ja: "続きを読む", nl: "Lees meer", fr: "Lire la suite" },
  "journal.back": { en: "Back to Journal", ja: "ジャーナルに戻る", nl: "Terug naar journaal", fr: "Retour au journal" },
  "journal.more_stories": { en: "More Stories", ja: "他の記事", nl: "Meer verhalen", fr: "Plus d'histoires" },
  "journal.not_found": { en: "Article Not Found", ja: "記事が見つかりません", nl: "Artikel niet gevonden", fr: "Article introuvable" },
  "journal.not_found_desc": {
    en: "This journal entry may have been removed or is not yet published.",
    ja: "この記事は削除されたか、まだ公開されていません。",
    nl: "Dit artikel is mogelijk verwijderd of nog niet gepubliceerd.",
    fr: "Cet article a peut-être été supprimé ou n'est pas encore publié.",
  },

  // Newsletter
  "newsletter.label": { en: "Stay Connected", ja: "つながりを保つ", nl: "Blijf verbonden", fr: "Restez connecté" },
  "newsletter.title": { en: "Join the Tea Ceremony", ja: "茶の湯に参加", nl: "Doe mee aan de theeceremonie", fr: "Rejoignez la cérémonie du thé" },
  "newsletter.desc": {
    en: "Receive stories, seasonal offerings, and exclusive access to limited releases.",
    ja: "物語、季節のご提供、限定リリースへの独占アクセスをお届けします。",
    nl: "Ontvang verhalen, seizoensaanbiedingen en exclusieve toegang tot gelimiteerde uitgaven.",
    fr: "Recevez des histoires, des offres saisonnières et un accès exclusif aux éditions limitées.",
  },
  "newsletter.placeholder": { en: "Your email address", ja: "メールアドレス", nl: "Je e-mailadres", fr: "Votre adresse e-mail" },
  "newsletter.subscribe": { en: "Subscribe", ja: "登録する", nl: "Abonneren", fr: "S'abonner" },
  "newsletter.success": { en: "Welcome to the ceremony.", ja: "茶の湯へようこそ。", nl: "Welkom bij de ceremonie.", fr: "Bienvenue à la cérémonie." },
  "newsletter.privacy": {
    en: "We respect your privacy. Unsubscribe anytime.",
    ja: "プライバシーを尊重します。いつでも解除可能です。",
    nl: "We respecteren je privacy. Schrijf je op elk moment uit.",
    fr: "Nous respectons votre vie privée. Désabonnez-vous à tout moment.",
  },

  // FAQ
  "faq.label": { en: "Knowledge", ja: "知識", nl: "Kennis", fr: "Savoir" },
  "faq.title": { en: "Matcha Guide & FAQ", ja: "抹茶ガイド＆FAQ", nl: "Matcha Gids & FAQ", fr: "Guide Matcha & FAQ" },

  // Footer
  "footer.crafted": { en: "Crafted with intention.", ja: "心を込めて作られた。", nl: "Met intentie gemaakt.", fr: "Créé avec intention." },
  "footer.location": { en: "Kyoto, Japan.", ja: "京都、日本。", nl: "Kyoto, Japan.", fr: "Kyoto, Japon." },
  "footer.rights": { en: "All rights reserved.", ja: "全著作権所有。", nl: "Alle rechten voorbehouden.", fr: "Tous droits réservés." },

  // Wishlist
  "wishlist.added": { en: "Added to wishlist", ja: "お気に入りに追加しました", nl: "Toegevoegd aan verlanglijst", fr: "Ajouté à la liste de souhaits" },
  "wishlist.removed": { en: "Removed from wishlist", ja: "お気に入りから削除しました", nl: "Verwijderd van verlanglijst", fr: "Retiré de la liste de souhaits" },

  // Scroll to top
  "scroll_top": { en: "Back to top", ja: "トップに戻る", nl: "Terug naar boven", fr: "Retour en haut" },

  // Search
  "search.title": { en: "Search", ja: "検索", nl: "Zoeken", fr: "Rechercher" },
  "search.placeholder": { en: "Search products, journal entries...", ja: "商品、記事を検索...", nl: "Zoek producten, artikelen...", fr: "Rechercher produits, articles..." },
  "search.products": { en: "Products", ja: "商品", nl: "Producten", fr: "Produits" },
  "search.journal_entries": { en: "Journal", ja: "ジャーナル", nl: "Journaal", fr: "Journal" },
  "search.no_results": { en: "No results found", ja: "結果が見つかりません", nl: "Geen resultaten gevonden", fr: "Aucun résultat trouvé" },
  "search.try_different": { en: "Try a different search term", ja: "別の検索語をお試しください", nl: "Probeer een andere zoekterm", fr: "Essayez un autre terme de recherche" },

  // Wholesale
  "wholesale.title": { en: "Wholesale Inquiries", ja: "卸売りのお問い合わせ", nl: "Groothandel aanvragen", fr: "Demandes de vente en gros" },

  // Account
  "account.title": { en: "My Account", ja: "マイアカウント", nl: "Mijn account", fr: "Mon compte" },
  "account.coming_soon": { en: "Customer accounts will be available soon. Connect with us for order inquiries.", ja: "お客様アカウントは近日公開予定です。ご注文に関するお問い合わせはお気軽にどうぞ。", nl: "Klantaccounts zijn binnenkort beschikbaar. Neem contact met ons op voor bestellingen.", fr: "Les comptes clients seront bientôt disponibles. Contactez-nous pour vos commandes." },
  "account.back_home": { en: "Back to Home", ja: "ホームに戻る", nl: "Terug naar home", fr: "Retour à l'accueil" },
  "account.sign_in": { en: "Sign In", ja: "ログイン", nl: "Inloggen", fr: "Se connecter" },
  "account.sign_up": { en: "Create Account", ja: "アカウント作成", nl: "Account aanmaken", fr: "Créer un compte" },
  "account.sign_in_desc": { en: "Welcome back. Sign in to view your orders.", ja: "おかえりなさい。注文を確認するにはログインしてください。", nl: "Welkom terug. Log in om je bestellingen te bekijken.", fr: "Bienvenue. Connectez-vous pour voir vos commandes." },
  "account.sign_up_desc": { en: "Create an account to track your orders.", ja: "注文を追跡するためにアカウントを作成してください。", nl: "Maak een account aan om je bestellingen te volgen.", fr: "Créez un compte pour suivre vos commandes." },
  "account.name": { en: "Name", ja: "名前", nl: "Naam", fr: "Nom" },
  "account.email": { en: "Email", ja: "メール", nl: "E-mail", fr: "E-mail" },
  "account.password": { en: "Password", ja: "パスワード", nl: "Wachtwoord", fr: "Mot de passe" },
  "account.no_account": { en: "Don't have an account?", ja: "アカウントをお持ちでないですか？", nl: "Geen account?", fr: "Pas de compte ?" },
  "account.have_account": { en: "Already have an account?", ja: "すでにアカウントをお持ちですか？", nl: "Heb je al een account?", fr: "Vous avez déjà un compte ?" },
  "account.sign_out": { en: "Sign Out", ja: "ログアウト", nl: "Uitloggen", fr: "Se déconnecter" },
  "account.orders": { en: "Orders", ja: "注文", nl: "Bestellingen", fr: "Commandes" },
  "account.profile": { en: "Profile", ja: "プロフィール", nl: "Profiel", fr: "Profil" },
  "account.no_orders": { en: "No orders yet. Start shopping!", ja: "まだ注文がありません。お買い物を始めましょう！", nl: "Nog geen bestellingen. Begin met winkelen!", fr: "Pas encore de commandes. Commencez vos achats !" },
  "account.member_since": { en: "Member since", ja: "会員登録日", nl: "Lid sinds", fr: "Membre depuis" },

  // Skip to content
  "skip_to_content": { en: "Skip to main content", ja: "メインコンテンツへスキップ", nl: "Ga naar hoofdinhoud", fr: "Aller au contenu principal" },
} as const;

type TranslationKey = keyof typeof translations;

interface LanguageContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: TranslationKey) => string;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

const LANG_STORAGE_KEY = "moecha_lang";
const VALID_LANGS: Lang[] = ["en", "ja", "nl", "fr"];

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    try {
      const stored = localStorage.getItem(LANG_STORAGE_KEY);
      return stored && VALID_LANGS.includes(stored as Lang) ? (stored as Lang) : "en";
    } catch {
      return "en";
    }
  });

  useEffect(() => {
    localStorage.setItem(LANG_STORAGE_KEY, lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = (l: Lang) => setLangState(l);

  const t = (key: TranslationKey): string => {
    const entry = translations[key];
    return entry ? entry[lang] : key;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}